import type { UserStatus as ApiUserStatus, UserRole } from '@/api/users';
import { Button, Card, CardContent, Input } from '@/components/ui';
import { useUsers } from '@/hooks/use-users';
import { useTranslation } from '@kgc/i18n';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MOCK_TENANTS } from './mock-data';

const ALL_ROLES: UserRole[] = [
  'OPERATOR',
  'TECHNIKUS',
  'BOLTVEZETO',
  'ACCOUNTANT',
  'PARTNER_OWNER',
  'CENTRAL_ADMIN',
  'DEVOPS_ADMIN',
  'SUPER_ADMIN',
];

/** Status config for display */

const ALL_STATUSES: ApiUserStatus[] = ['ACTIVE', 'INACTIVE', 'LOCKED', 'PENDING_VERIFICATION'];

export function UserListPage() {
  const { t } = useTranslation();

  const STATUS_CONFIG: Record<ApiUserStatus, { label: string; color: string }> = {
    ACTIVE: {
      label: t('user.statusLabels.ACTIVE'),
      color: 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300',
    },
    INACTIVE: {
      label: t('user.statusLabels.INACTIVE'),
      color: 'bg-gray-100 text-gray-800 dark:bg-[#1f5f6b] dark:text-[#ccfbf1]',
    },
    LOCKED: {
      label: t('user.statusLabels.LOCKED'),
      color: 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300',
    },
    PENDING_VERIFICATION: {
      label: t('user.statusLabels.PENDING_VERIFICATION'),
      color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-300',
    },
  };
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<ApiUserStatus | 'all'>('all');
  const [roleFilter, setRoleFilter] = useState<UserRole | 'all'>('all');
  const [tenantFilter, setTenantFilter] = useState<string>('all');

  // Build API filters - only send defined values
  const apiFilters = useMemo(() => {
    const filters: { search?: string; role?: UserRole; status?: ApiUserStatus } = {};
    if (searchTerm.trim()) filters.search = searchTerm.trim();
    if (roleFilter !== 'all') filters.role = roleFilter;
    if (statusFilter !== 'all') filters.status = statusFilter;
    return filters;
  }, [searchTerm, roleFilter, statusFilter]);

  const { users, total, isLoading, error } = useUsers(apiFilters);

  // Client-side tenant filter (no tenant listing API)
  const filteredUsers = useMemo(() => {
    if (tenantFilter === 'all') return users;
    return users.filter(user => user.tenantId === tenantFilter);
  }, [users, tenantFilter]);

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('hu-HU', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusBadge = (status: ApiUserStatus) => {
    const config = STATUS_CONFIG[status];
    return (
      <span className={`rounded-full px-2 py-1 text-xs font-medium ${config?.color ?? ''}`}>
        {config?.label ?? status}
      </span>
    );
  };

  const getRoleBadge = (role: UserRole) => {
    return (
      <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800 dark:bg-blue-900/20 dark:text-blue-300">
        {t(`user.roleLabels.${role}`)}
      </span>
    );
  };

  /** Extract initials from the name field */
  const getInitials = (name: string) => {
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) {
      return `${(parts[0] ?? '')[0] ?? ''}${(parts[1] ?? '')[0] ?? ''}`;
    }
    return (name[0] ?? '?').toUpperCase();
  };

  // Stats from loaded data
  const stats = {
    total,
    active: users.filter(u => u.status === 'ACTIVE').length,
    inactive: users.filter(u => u.status === 'INACTIVE').length,
    locked: users.filter(u => u.status === 'LOCKED').length,
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 dark:bg-[#0f3d47]">
        <div className="text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-primary border-r-transparent" />
          <p className="mt-2 text-gray-500 dark:text-[#ccfbf1]">{t('user.loadingUsers')}</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 dark:bg-[#0f3d47]">
        <Card className="p-8 text-center">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
            {t('user.errorOccurred')}
          </h2>
          <p className="text-gray-500 dark:text-[#ccfbf1] mb-4">{error}</p>
          <Button onClick={() => navigate('/dashboard')}>{t('user.backToDashboard')}</Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-[#0f3d47]">
      {/* Header */}
      <header className="bg-white shadow-sm dark:bg-[#1a4f5c]">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3">
            <Button variant="ghost" onClick={() => navigate('/dashboard')}>
              {t('common.back')}
            </Button>
            <div>
              <h1 className="text-xl font-bold text-gray-900 dark:text-white">{t('user.title')}</h1>
              <p className="text-sm text-gray-500 dark:text-[#ccfbf1]">{t('user.subtitle')}</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate('/users/roles')}>
              {t('user.roles')}
            </Button>
            <Button
              onClick={() => navigate('/users/new')}
              className="bg-primary hover:bg-primary/90"
            >
              {t('user.newUser')}
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        {/* Stats cards */}
        <div className="mb-6 grid grid-cols-2 gap-4 sm:grid-cols-4">
          <Card>
            <CardContent className="pt-4">
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.total}</p>
              <p className="text-sm text-gray-500 dark:text-[#ccfbf1]">{t('user.totalUsers')}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                {stats.active}
              </p>
              <p className="text-sm text-gray-500 dark:text-[#ccfbf1]">
                {t('user.statusLabels.ACTIVE')}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <p className="text-2xl font-bold text-gray-600 dark:text-[#ccfbf1]">
                {stats.inactive}
              </p>
              <p className="text-sm text-gray-500 dark:text-[#ccfbf1]">
                {t('user.statusLabels.INACTIVE')}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <p className="text-2xl font-bold text-red-600 dark:text-red-400">{stats.locked}</p>
              <p className="text-sm text-gray-500 dark:text-[#ccfbf1]">
                {t('user.statusLabels.LOCKED')}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="pt-4">
            <div className="flex flex-wrap items-center gap-4">
              <div className="min-w-[250px] flex-1">
                <Input
                  placeholder={t('user.searchPlaceholder')}
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                />
              </div>
              <select
                value={statusFilter}
                onChange={e => setStatusFilter(e.target.value as ApiUserStatus | 'all')}
                className="rounded-md border px-3 py-2 dark:border-[#2d7d8a] dark:bg-[#1a4f5c] dark:text-white"
              >
                <option value="all">{t('page.allStatuses')}</option>
                {ALL_STATUSES.map(s => (
                  <option key={s} value={s}>
                    {STATUS_CONFIG[s].label}
                  </option>
                ))}
              </select>
              <select
                value={roleFilter}
                onChange={e => setRoleFilter(e.target.value as UserRole | 'all')}
                className="rounded-md border px-3 py-2 dark:border-[#2d7d8a] dark:bg-[#1a4f5c] dark:text-white"
              >
                <option value="all">{t('user.allRoles')}</option>
                {ALL_ROLES.map(r => (
                  <option key={r} value={r}>
                    {t(`user.roleLabels.${r}`)}
                  </option>
                ))}
              </select>
              <select
                value={tenantFilter}
                onChange={e => setTenantFilter(e.target.value)}
                className="rounded-md border px-3 py-2 dark:border-[#2d7d8a] dark:bg-[#1a4f5c] dark:text-white"
              >
                <option value="all">{t('user.allStores')}</option>
                {MOCK_TENANTS.map(tenant => (
                  <option key={tenant.id} value={tenant.id}>
                    {tenant.name}
                  </option>
                ))}
              </select>
            </div>
          </CardContent>
        </Card>

        {/* Users table */}
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="border-b bg-gray-50 dark:bg-[#1f5f6b] dark:border-[#2d7d8a]">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 dark:text-[#ccfbf1]">
                      {t('user.userColumn')}
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 dark:text-[#ccfbf1]">
                      {t('user.roleColumn')}
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 dark:text-[#ccfbf1]">
                      {t('user.createdAtColumn')}
                    </th>
                    <th className="px-4 py-3 text-center text-sm font-medium text-gray-500 dark:text-[#ccfbf1]">
                      {t('table.status')}
                    </th>
                    <th className="px-4 py-3 text-right text-sm font-medium text-gray-500 dark:text-[#ccfbf1]"></th>
                  </tr>
                </thead>
                <tbody className="divide-y dark:divide-[#2d7d8a]">
                  {filteredUsers.map(user => (
                    <tr
                      key={user.id}
                      className="cursor-pointer hover:bg-gray-50 dark:hover:bg-[#0f3d47]"
                      onClick={() => navigate(`/users/${user.id}`)}
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 font-medium text-primary">
                            {getInitials(user.name)}
                          </div>
                          <div>
                            <p className="font-medium text-gray-900 dark:text-white">{user.name}</p>
                            <p className="text-sm text-gray-500 dark:text-[#ccfbf1]">
                              {user.email}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1">{getRoleBadge(user.role)}</div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-gray-600 dark:text-[#ccfbf1]">
                          {formatDate(user.createdAt)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">{getStatusBadge(user.status)}</td>
                      <td className="px-4 py-3 text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={e => {
                            e.stopPropagation();
                            navigate(`/users/${user.id}`);
                          }}
                        >
                          {t('common.details')}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {filteredUsers.length === 0 && (
                <div className="py-12 text-center text-gray-500 dark:text-[#ccfbf1]">
                  <p>{t('page.noFilterResults')}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <p className="mt-4 text-sm text-gray-500 dark:text-[#ccfbf1]">
          {t('user.usersDisplayed', { count: filteredUsers.length })}
        </p>
      </main>
    </div>
  );
}
